/** Agent overview: one agent, everything that shapes it, and where each part is changed. */
export default {
  en: {
    title: "Agent overview",
    subtitle: "One agent in one place: where people reach it, what it uses, the rules it follows. Each line says where it is changed.",
    pick: "Choose an agent",
    pickHelp: "Pick an agent to see everything about it.",
    none: "No agents yet.",
    open: "Agent overview",
    openAria: "Open the overview of {{agent}}",
    changeIn: "Change in {{place}}",
    changedBy: "Changed by {{who}}",
    hidden: "Your roles don't include this part. Ask an administrator if you need it.",
    failed: "Couldn't load this part right now.",
    yes: "Yes",
    no: "No",
    place: {
      builder: "Builder",
      triggers: "Builder › When it runs",
      widgets: "Channels › Website chat",
      connect: "Channels › Connect apps",
      voice: "Channels › Voice",
      chat: "Assistant",
      models: "Models › Model choices",
      speech: "Models › Speech",
      tools: "Administration › Tools",
      approvals: "Administration › Approval amounts",
      knowledge: "Knowledge",
      releases: "Governance › Releases",
      rules: "Governance › Rules",
      privacy: "Privacy › Privacy and guardrails",
      spending: "Administration › Spending limits",
      stop: "Governance › Agents",
      quality: "Quality",
    },
    who: {
      builder: "agent builders",
      channels: "channel administrators",
      models: "model administrators",
      tools: "the platform team (tool administrators)",
      approvals: "organisation administrators",
      knowledge: "knowledge curators",
      releases: "release approvers",
      platform: "the platform team",
      privacy: "privacy officers",
      spending: "organisation administrators",
      stop: "the agent's owners and organisation administrators",
    },
    limits: {
      heading: "Who may use it and its limits",
      audience: "Who may use it",
      groups: "Only the groups {{groups}}",
      everyone: "Everyone who may use agents in this organisation",
      perRequest: "Limits of one request",
      steps_one: "At most {{count}} step",
      steps_other: "At most {{count}} steps",
      time: "At most {{minutes}} minutes",
      tokens: "At most {{count}} tokens of AI text",
      cost: "At most {{amount}}",
      onItsOwn: "Works on its own",
      onItsOwnYes: "Yes: its schedule or another system can start it with nobody signed in",
      onItsOwnNo: "No: a person always starts it",
    },
    compare: {
      heading: "What changed between versions",
      from: "From",
      to: "to",
      same: "These versions are the same.",
      yes: "yes",
      no: "no",
      stepAdded: "Step added: {{step}}",
      stepRemoved: "Step removed: {{step}}",
      stepChanged: "Step changed: {{step}} ({{what}})",
      instructions: "New instructions for {{step}}:",
      arrows: "Arrows between steps: {{added}} added, {{removed}} removed",
      tools: "Systems it may use: added {{added}}; removed {{removed}}",
      setting: "{{setting}}: {{before}} → {{after}}",
      field: { instructions: "instructions", model: "AI model", system: "system and what it sends", collection: "knowledge searched", question: "question asked", approvers: "who approves", settings: "other settings" },
      settingName: {
        description: "What it does",
        role: "Its role",
        goals: "Goals",
        model: "AI model",
        risk_tier: "Risk level",
        data_level: "Data level",
        autonomous: "Works on its own",
        runner_groups: "Who may use it",
        max_steps: "Steps per request",
        max_cost_usd: "Spending limit per request (USD)",
        max_tokens: "AI text per request",
        timeout: "Time limit",
        knowledge: "Knowledge it searches",
        triggers: "When it runs",
        approvals: "Approval rules",
        memory: "Memory",
      },
    },
    about: {
      versionValue: "Version {{version}}",
      inStage: "{{stage}}: version {{version}}",
      heading: "About",
      owner: "Owner",
      risk: "Risk level",
      dataLevel: "Data level",
      riskValue: "{{tier}} · {{label}}",
      riskLabel: { "1": "low", "2": "medium", "3": "high", "4": "critical" },
      version: "Newest version",
      release: "Released to",
      releaseNone: "Not released yet",
      pending: "waiting for sign-off to move to {{env}}",
      status: "Status",
      status_active: "Running",
      status_suspended: "Paused",
      status_retired: "Retired",
    },
    reachPanel: {
      title: "It's live. How will people reach it?",
      intro: "People reach an agent only where you put it. Today:",
      assistant: { on: "In the Assistant", off: "Not in the Assistant yet" },
      widgets: { on: "On a website chat", off: "Not on any website yet", action: "Add a website chat" },
      connect: { on: "In a chat app or e-mail", off: "Not in WhatsApp, Teams or e-mail yet", action: "Connect an app" },
      voice: { on: "People can talk to it", off: "Voice is off", action: "Turn on voice" },
      unknown: "a channel administrator can tell you",
      overview: "See everything about this agent",
      cardLink: "How people reach it",
    },
    reach: {
      heading: "Where people reach it",
      assistant: "In the Assistant",
      assistantYes: "Yes, people can ask it in the Assistant",
      assistantNo: "Not listed in the Assistant (no version released to Development yet)",
      widgets: "Website chat",
      widgetsNone: "No website chat uses it",
      widgetOn: "{{name}} on {{sites}}",
      apps: "Chat apps and e-mail",
      appsNone: "No chat app or e-mail is connected to it",
      appOn: "{{name}} ({{app}})",
      off: "turned off",
      voice: "Voice",
      voiceOff: "Voice is off",
      voiceOn: "On · {{langs}}",
      voiceVoices: "Voices: {{list}}",
      voicePrivate: "Private: speech stays on our own servers",
    },
    models: {
      heading: "AI models",
      agent: "The agent's model",
      fallback: "If that model can't answer",
      step: "Step “{{step}}”",
      where: { local: "runs on our own servers", online: "runs online", mixed: "online, with a private copy on our own servers" },
      share: "{{model}} · {{share}}%",
      quiet: "{{model}} · private conversations only",
      backups: "Backups: {{list}}",
      noBackups: "No backup models",
      details: "Model administrators also see which models are behind each name.",
    },
    tools: {
      heading: "Systems and websites it may use",
      none: "No systems",
      actions: "{{list}}",
      sites: "May open: {{list}}",
      sitesNone: "No website allowed yet",
      sitesHidden: "The allowed websites are visible to builders and the platform team.",
    },
    approvals: {
      heading: "When a person must approve",
      rule: "{{action}} in {{tool}}",
      ruleWhen: "only when {{when}}",
      by: "approved by {{roles}}",
      stepLabel: "Step “{{step}}”",
      none: "No approval rules",
      amount: "Money limit for risk level {{tier}}",
      amountValue: "Above {{amount}} a person approves ({{source}})",
      source: { yours: "your organisation's limit", platform: "the platform's limit" },
      amountNone: "No money limit for this risk level",
      always: "Risk level {{tier}} and above: every action that changes something is approved by a person",
    },
    knowledge: {
      heading: "Knowledge it searches",
      none: "No knowledge collections",
      topK: "up to {{count}} passages",
    },
    starts: {
      heading: "How it starts",
    },
    rules: {
      heading: "Rules and safety",
      intro: "What this agent may do, and what stops it. Each part is enforced by the platform on every request, and each check is in the audit trail.",
      dataLevel: "Data level",
      dataLevelFinds: "It only reads documents and data up to this level.",
      access: "Access rules",
      accessText: "Checked before every action: who may ask it, which systems it may use, and what needs a second person.",
      safety: "Privacy and guardrails",
      safetyNone: "No checks are switched on.",
      input: "Before the AI sees a message",
      output: "Before an answer leaves",
      safetyItem: "{{category}}: {{action}}",
      action: { block: "blocked", redact: "hidden", flag: "flagged" },
      agentLayer: "This agent has its own settings on top of the organisation's.",
      approvals: "Approvals",
      approvalsText: "See Approvals above",
      spending: "Spending limit",
      limit_agent: "This agent: {{amount}} per {{period}}",
      limit_tenant: "Your organisation: {{amount}} per {{period}}",
      limitNone: "No spending limit",
      soft: "a warning only, it doesn't stop",
      tokens: "{{count}} tokens",
      period: { day: "day", month: "month" },
      stop: "Can it run now?",
      stopState: {
        running: "Yes. Its owners can pause it, and administrators can use an emergency stop.",
        paused: "No: it's paused. Its owners can resume it.",
        agent: "No: an emergency stop is on for this agent.",
        tenant: "No: an emergency stop is on for your whole organisation.",
        global: "No: an emergency stop is on for the whole platform.",
      },
      quality: "Last test result",
      qualityPassed: "Passed {{suite}} (version {{version}}, {{at}})",
      qualityFailed: "Failed {{suite}} (version {{version}}, {{at}})",
      qualityNone: "Not tested yet",
      orgRules: "Your organisation's rules",
      orgRulesNone: "None of your organisation's own rules apply to this agent.",
    },
    code: {
      heading: "Use this agent from code",
      intro: "Other programs can ask this agent through an OpenAI-compatible address, and AI apps (for example Claude Desktop or Cursor) can use it as a tool over MCP. Each call is an ordinary request: the same rules, approvals, spending limit and audit apply.",
      ready: "Ready: it is released and may answer without a signed-in person.",
      notReleased: "Release a version to Development first.",
      noWebhook: "It cannot answer without a signed-in person yet: create its webhook key in Builder › When it runs.",
      keys: "API keys for this agent",
      keysNone: "No API key uses this agent yet.",
      keysHidden: "Only channel administrators create and see API keys.",
      keyLine: "{{name}} · …{{hint}} · {{status}}",
      expires: "expires {{date}}",
      never: "never expires",
      lastUsed: "last used {{date}}",
      notUsed: "not used yet",
      status: { active: "active", revoked: "revoked", expired: "expired" },
      revoke: "Revoke",
      revokeAria: "Revoke the API key {{name}}",
      revokeTitle: "Revoke this key? Programs using it stop working at once.",
      revoked: "Key revoked",
      create: "Create an API key",
      name: "Key name",
      namePlaceholder: "for example: reporting script",
      version: "Version it uses",
      expiry: "Expires after",
      days: { "30": "30 days", "90": "90 days", "365": "1 year", never: "Never" },
      createButton: "Create key",
      createdTitle: "Copy the key now",
      createdBody: "This is the only time the key is shown. Store it in the program's settings (for example as AP_API_KEY). If it is lost, revoke it and create a new one.",
      copied: "Copied",
      done: "I have stored it",
      snippets: "Ready-to-copy examples",
      tab: { curl: "curl", python: "Python", javascript: "JavaScript", mcp: "MCP (AI apps)" },
      a2a: "Agent-to-agent (A2A): the SDK can produce this agent's card (ap card); calling agents over A2A is not available yet.",
      newVersion: "A key names one version. After a new release, create a key for it (or keep the old version).",
    },
    // Stream C (ADR-0101): work the agent may do on its own (roles of its own identity).
    work: {
      heading: "Work it may do on its own",
      intro: "When nobody is signed in (for example, a website visitor), the agent acts as itself. These switches decide which work it may then do.",
      role: {
        "case-worker": "Open and update cases",
        "case-supervisor": "Work on cases as a supervisor",
      },
      roleHelp: {
        "case-worker": "Lets it open, update and hand over cases, for example when a website visitor asks for help.",
        "case-supervisor": "Also lets it work on cases in every team's queue.",
      },
      needsCases: "This agent opens or changes cases. Without \"Open and update cases\" it can't do that when nobody is signed in.",
      applies: "Changes apply to new conversations within five minutes. Each change is recorded in the activity log.",
      saved: "Saved.",
      failed: "Couldn't save: {{message}}",
      noIdentity: "The agent gets its own identity when it is first released. Release it, then come back here.",
      who: "Only an organisation admin or an agent admin can see and change this.",
    },
  },
  ar: {
    title: "نظرة عامة على الوكيل",
    subtitle: "الوكيل في مكان واحد: أين يصل إليه الناس، وما يستخدمه، والقواعد التي يتبعها. كل سطر يوضح أين يُغيَّر.",
    pick: "اختر وكيلًا",
    pickHelp: "اختر وكيلًا لترى كل ما يخصه.",
    none: "لا يوجد وكلاء بعد.",
    open: "نظرة عامة على الوكيل",
    openAria: "افتح النظرة العامة على {{agent}}",
    changeIn: "يُغيَّر في {{place}}",
    changedBy: "يغيّره {{who}}",
    hidden: "أدوارك لا تشمل هذا الجزء. اطلبه من مسؤول إن احتجت إليه.",
    failed: "تعذّر تحميل هذا الجزء الآن.",
    yes: "نعم",
    no: "لا",
    place: {
      builder: "المُنشئ",
      triggers: "المُنشئ › متى يعمل",
      widgets: "القنوات › محادثة الموقع",
      connect: "القنوات › ربط التطبيقات",
      voice: "القنوات › الصوت",
      chat: "المساعد",
      models: "النماذج › خيارات النماذج",
      speech: "النماذج › الكلام",
      tools: "الإدارة › الأدوات",
      approvals: "الإدارة › مبالغ الاعتماد",
      knowledge: "المعرفة",
      releases: "الحوكمة › الإصدارات",
      rules: "الحوكمة › القواعد",
      privacy: "الخصوصية › الخصوصية والضوابط الوقائية",
      spending: "الإدارة › حدود الإنفاق",
      stop: "الحوكمة › الوكلاء",
      quality: "الجودة",
    },
    who: {
      builder: "مُنشئو الوكلاء",
      channels: "مسؤولو القنوات",
      models: "مسؤولو النماذج",
      tools: "فريق المنصة (مسؤولو الأدوات)",
      approvals: "مسؤولو الجهة",
      knowledge: "أمناء المعرفة",
      releases: "معتمدو الإصدارات",
      platform: "فريق المنصة",
      privacy: "مسؤولو الخصوصية",
      spending: "مسؤولو الجهة",
      stop: "مالكو الوكيل ومسؤولو الجهة",
    },
    limits: {
      heading: "من يستخدمه وحدوده",
      audience: "من يمكنه استخدامه",
      groups: "المجموعات {{groups}} فقط",
      everyone: "كل من يُسمح له باستخدام الوكلاء في هذه المؤسسة",
      perRequest: "حدود الطلب الواحد",
      steps_zero: "{{count}} خطوة كحد أقصى",
      steps_one: "خطوة واحدة كحد أقصى",
      steps_two: "خطوتان كحد أقصى",
      steps_few: "{{count}} خطوات كحد أقصى",
      steps_many: "{{count}} خطوة كحد أقصى",
      steps_other: "{{count}} خطوة كحد أقصى",
      time: "{{minutes}} دقيقة كحد أقصى",
      tokens: "{{count}} رمزًا من نص الذكاء الاصطناعي كحد أقصى",
      cost: "{{amount}} كحد أقصى",
      onItsOwn: "يعمل وحده",
      onItsOwnYes: "نعم: يمكن لجدوله أو لنظام آخر تشغيله دون أن يسجّل أحد دخوله",
      onItsOwnNo: "لا: يبدأه شخص دائمًا",
    },
    compare: {
      heading: "ما الذي تغيّر بين الإصدارات",
      from: "من",
      to: "إلى",
      same: "هذان الإصداران متطابقان.",
      yes: "نعم",
      no: "لا",
      stepAdded: "أُضيفت خطوة: {{step}}",
      stepRemoved: "حُذفت خطوة: {{step}}",
      stepChanged: "تغيّرت خطوة: {{step}} ({{what}})",
      instructions: "تعليمات جديدة لـ {{step}}:",
      arrows: "الأسهم بين الخطوات: أُضيف {{added}}، وحُذف {{removed}}",
      tools: "الأنظمة التي يمكنه استخدامها: أُضيف {{added}}؛ وحُذف {{removed}}",
      setting: "{{setting}}: {{before}} ← {{after}}",
      field: { instructions: "التعليمات", model: "نموذج الذكاء الاصطناعي", system: "النظام وما يُرسل إليه", collection: "المعرفة التي يبحث فيها", question: "السؤال المطروح", approvers: "من يعتمد", settings: "إعدادات أخرى" },
      settingName: {
        description: "ما يفعله",
        role: "دوره",
        goals: "الأهداف",
        model: "نموذج الذكاء الاصطناعي",
        risk_tier: "مستوى المخاطر",
        data_level: "مستوى البيانات",
        autonomous: "يعمل وحده",
        runner_groups: "من يمكنه استخدامه",
        max_steps: "الخطوات لكل طلب",
        max_cost_usd: "حد الإنفاق لكل طلب (دولار)",
        max_tokens: "نص الذكاء الاصطناعي لكل طلب",
        timeout: "المهلة",
        knowledge: "المعرفة التي يبحث فيها",
        triggers: "متى يعمل",
        approvals: "قواعد الاعتماد",
        memory: "الذاكرة",
      },
    },
    about: {
      versionValue: "الإصدار {{version}}",
      inStage: "{{stage}}: الإصدار {{version}}",
      heading: "عن الوكيل",
      owner: "المالك",
      risk: "مستوى الخطورة",
      dataLevel: "مستوى البيانات",
      riskValue: "{{tier}} · {{label}}",
      riskLabel: { "1": "منخفض", "2": "متوسط", "3": "عالٍ", "4": "حرج" },
      version: "أحدث إصدار",
      release: "مُطلق إلى",
      releaseNone: "لم يُطلق بعد",
      pending: "بانتظار الموافقة للانتقال إلى {{env}}",
      status: "الحالة",
      status_active: "يعمل",
      status_suspended: "متوقف مؤقتًا",
      status_retired: "متقاعد",
    },
    reachPanel: {
      title: "أصبح الوكيل متاحًا. كيف سيصل إليه الناس؟",
      intro: "لا يصل الناس إلى الوكيل إلا حيث تضعه. حاليًا:",
      assistant: { on: "في المساعد", off: "ليس في المساعد بعد" },
      widgets: { on: "في محادثة على موقع", off: "ليس على أي موقع بعد", action: "أضف محادثة موقع" },
      connect: { on: "في تطبيق محادثة أو بريد إلكتروني", off: "ليس في واتساب أو Teams أو البريد بعد", action: "اربط تطبيقًا" },
      voice: { on: "يمكن التحدث معه", off: "الصوت متوقف", action: "شغّل الصوت" },
      unknown: "يستطيع مسؤول القنوات إخبارك",
      overview: "اطّلع على كل ما يخص هذا الوكيل",
      cardLink: "كيف يصل إليه الناس",
    },
    reach: {
      heading: "أين يصل إليه الناس",
      assistant: "في المساعد",
      assistantYes: "نعم، يمكن للناس سؤاله في المساعد",
      assistantNo: "غير مُدرج في المساعد (لم يُطلق أي إصدار إلى التطوير بعد)",
      widgets: "محادثة الموقع",
      widgetsNone: "لا توجد محادثة موقع تستخدمه",
      widgetOn: "{{name}} على {{sites}}",
      apps: "تطبيقات المحادثة والبريد",
      appsNone: "لا يوجد تطبيق محادثة أو بريد مرتبط به",
      appOn: "{{name}} ({{app}})",
      off: "مُعطّل",
      voice: "الصوت",
      voiceOff: "الصوت مُعطّل",
      voiceOn: "مُفعّل · {{langs}}",
      voiceVoices: "الأصوات: {{list}}",
      voicePrivate: "خاص: يبقى الكلام على خوادمنا",
    },
    models: {
      heading: "نماذج الذكاء الاصطناعي",
      agent: "نموذج الوكيل",
      fallback: "إذا لم يستطع ذلك النموذج الإجابة",
      step: "الخطوة «{{step}}»",
      where: { local: "يعمل على خوادمنا", online: "يعمل عبر الإنترنت", mixed: "عبر الإنترنت، مع نسخة خاصة على خوادمنا" },
      share: "{{model}} · {{share}}%",
      quiet: "{{model}} · للمحادثات الخاصة فقط",
      backups: "النماذج الاحتياطية: {{list}}",
      noBackups: "لا توجد نماذج احتياطية",
      details: "يرى مسؤولو النماذج أيضًا النماذج التي تقف خلف كل اسم.",
    },
    tools: {
      heading: "الأنظمة والمواقع التي يمكنه استخدامها",
      none: "لا توجد أنظمة",
      actions: "{{list}}",
      sites: "يمكنه فتح: {{list}}",
      sitesNone: "لا يوجد موقع مسموح به بعد",
      sitesHidden: "المواقع المسموح بها يراها المُنشئون وفريق المنصة.",
    },
    approvals: {
      heading: "متى يجب أن يعتمد شخص",
      rule: "{{action}} في {{tool}}",
      ruleWhen: "فقط عندما {{when}}",
      by: "يعتمده {{roles}}",
      stepLabel: "الخطوة «{{step}}»",
      none: "لا توجد قواعد اعتماد",
      amount: "الحد المالي لمستوى الخطورة {{tier}}",
      amountValue: "فوق {{amount}} يعتمد شخص ({{source}})",
      source: { yours: "حد جهتك", platform: "حد المنصة" },
      amountNone: "لا يوجد حد مالي لهذا المستوى",
      always: "مستوى الخطورة {{tier}} وما فوقه: كل إجراء يغيّر شيئًا يعتمده شخص",
    },
    knowledge: {
      heading: "المعرفة التي يبحث فيها",
      none: "لا توجد مجموعات معرفة",
      topK: "حتى {{count}} مقاطع",
    },
    starts: {
      heading: "كيف يبدأ",
    },
    rules: {
      heading: "القواعد والأمان",
      intro: "ما يُسمح لهذا الوكيل بفعله، وما يوقفه. تفرض المنصة كل جزء منها في كل طلب، ويُسجَّل كل فحص في سجل التدقيق.",
      dataLevel: "مستوى البيانات",
      dataLevelFinds: "لا يقرأ إلا المستندات والبيانات حتى هذا المستوى.",
      access: "قواعد الوصول",
      accessText: "تُفحص قبل كل إجراء: من يمكنه سؤاله، وأي الأنظمة يمكنه استخدامها، وما الذي يحتاج إلى شخص ثانٍ.",
      safety: "فحوص الخصوصية والأمان",
      safetyNone: "لا توجد فحوص مفعّلة.",
      input: "قبل أن يرى الذكاء الاصطناعي الرسالة",
      output: "قبل خروج الإجابة",
      safetyItem: "{{category}}: {{action}}",
      action: { block: "يُمنع", redact: "يُخفى", flag: "يُنبَّه عليه" },
      agentLayer: "لهذا الوكيل إعداداته الخاصة فوق إعدادات الجهة.",
      approvals: "الاعتمادات",
      approvalsText: "انظر الاعتمادات أعلاه",
      spending: "حد الإنفاق",
      limit_agent: "هذا الوكيل: {{amount}} لكل {{period}}",
      limit_tenant: "جهتك: {{amount}} لكل {{period}}",
      limitNone: "لا يوجد حد إنفاق",
      soft: "تنبيه فقط، لا يوقف",
      tokens: "{{count}} رمزًا",
      period: { day: "يوم", month: "شهر" },
      stop: "هل يمكنه العمل الآن؟",
      stopState: {
        running: "نعم. يمكن لمالكيه إيقافه مؤقتًا، ويمكن للمسؤولين استخدام الإيقاف الطارئ.",
        paused: "لا: هو موقوف مؤقتًا. يمكن لمالكيه استئنافه.",
        agent: "لا: الإيقاف الطارئ مفعّل لهذا الوكيل.",
        tenant: "لا: الإيقاف الطارئ مفعّل لجهتك كلها.",
        global: "لا: الإيقاف الطارئ مفعّل للمنصة كلها.",
      },
      quality: "آخر نتيجة اختبار",
      qualityPassed: "نجح في {{suite}} (الإصدار {{version}}، {{at}})",
      qualityFailed: "أخفق في {{suite}} (الإصدار {{version}}، {{at}})",
      qualityNone: "لم يُختبر بعد",
      orgRules: "قواعد جهتك",
      orgRulesNone: "لا تنطبق على هذا الوكيل أي من قواعد جهتك الخاصة.",
    },
    code: {
      heading: "استخدام هذا الوكيل من البرامج",
      intro: "يمكن للبرامج الأخرى سؤال هذا الوكيل عبر عنوان متوافق مع OpenAI، ويمكن لتطبيقات الذكاء الاصطناعي (مثل Claude Desktop أو Cursor) استخدامه كأداة عبر MCP. كل طلب هو طلب عادي: تنطبق عليه القواعد والاعتمادات وحد الإنفاق والتدقيق نفسها.",
      ready: "جاهز: أُطلق ويمكنه الإجابة دون شخص مسجّل الدخول.",
      notReleased: "أطلق إصدارًا إلى مرحلة التطوير أولًا.",
      noWebhook: "لا يمكنه الإجابة دون شخص مسجّل الدخول بعد: أنشئ مفتاح الويب هوك الخاص به في المنشئ › متى يعمل.",
      keys: "مفاتيح API لهذا الوكيل",
      keysNone: "لا يوجد مفتاح API يستخدم هذا الوكيل بعد.",
      keysHidden: "ينشئ مسؤولو القنوات فقط مفاتيح API ويرونها.",
      keyLine: "{{name}} · …{{hint}} · {{status}}",
      expires: "ينتهي في {{date}}",
      never: "لا ينتهي",
      lastUsed: "آخر استخدام {{date}}",
      notUsed: "لم يُستخدم بعد",
      status: { active: "فعّال", revoked: "ملغى", expired: "منتهي" },
      revoke: "إلغاء",
      revokeAria: "إلغاء مفتاح API {{name}}",
      revokeTitle: "إلغاء هذا المفتاح؟ تتوقف البرامج التي تستخدمه فورًا.",
      revoked: "أُلغي المفتاح",
      create: "إنشاء مفتاح API",
      name: "اسم المفتاح",
      namePlaceholder: "مثلًا: برنامج التقارير",
      version: "الإصدار الذي يستخدمه",
      expiry: "ينتهي بعد",
      days: { "30": "30 يومًا", "90": "90 يومًا", "365": "سنة", never: "لا ينتهي" },
      createButton: "إنشاء المفتاح",
      createdTitle: "انسخ المفتاح الآن",
      createdBody: "هذه هي المرة الوحيدة التي يظهر فيها المفتاح. احفظه في إعدادات البرنامج (مثلًا باسم AP_API_KEY). إذا ضاع، ألغِه وأنشئ مفتاحًا جديدًا.",
      copied: "نُسخ",
      done: "حفظته",
      snippets: "أمثلة جاهزة للنسخ",
      tab: { curl: "curl", python: "بايثون", javascript: "جافاسكربت", mcp: "MCP (تطبيقات الذكاء الاصطناعي)" },
      a2a: "من وكيل إلى وكيل (A2A): يمكن لحزمة SDK إنتاج بطاقة هذا الوكيل (ap card)؛ استدعاء الوكلاء عبر A2A غير متاح بعد.",
      newVersion: "يحدد المفتاح إصدارًا واحدًا. بعد إطلاق إصدار جديد، أنشئ مفتاحًا له (أو أبقِ الإصدار القديم).",
    },
    // Stream C (ADR-0101): work the agent may do on its own (roles of its own identity).
    work: {
      heading: "العمل الذي يمكنه القيام به وحده",
      intro: "عندما لا يكون أحد مسجّل الدخول (مثل زائر الموقع)، يعمل الوكيل بهويته الخاصة. تحدد هذه المفاتيح العمل الذي يُسمح له به حينها.",
      role: {
        "case-worker": "فتح الحالات وتحديثها",
        "case-supervisor": "العمل على الحالات كمشرف",
      },
      roleHelp: {
        "case-worker": "يسمح له بفتح الحالات وتحديثها وتحويلها، مثلاً عندما يطلب زائر الموقع المساعدة.",
        "case-supervisor": "يسمح له أيضًا بالعمل على الحالات في قوائم جميع الفرق.",
      },
      needsCases: "هذا الوكيل يفتح الحالات أو يغيّرها. بدون «فتح الحالات وتحديثها» لا يستطيع ذلك عندما لا يكون أحد مسجّل الدخول.",
      applies: "تسري التغييرات على المحادثات الجديدة خلال خمس دقائق. يُسجَّل كل تغيير في سجل النشاط.",
      saved: "تم الحفظ.",
      failed: "تعذّر الحفظ: {{message}}",
      noIdentity: "يحصل الوكيل على هويته الخاصة عند إصداره لأول مرة. أصدِره ثم عد إلى هنا.",
      who: "لا يمكن رؤية هذا وتغييره إلا لمسؤول المؤسسة أو مسؤول الوكلاء.",
    },
  },
};
